import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import type { MarginFloors } from '../lib/margin';

/**
 * The single web-side source for the owner's live, editable config floors. It fetches
 * GET /api/config once per authenticated session (mirrors AuthProvider) so the margin
 * traffic-light everywhere tracks the owner's CONFIGURED redFloor/amberFloor instead of
 * a stale hardcoded literal. Until the fetch lands — or if it fails — the floors are
 * undefined, which makes marginColor() render neutral rather than guess.
 */
interface ConfigContextValue {
  floors: MarginFloors;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [floors, setFloors] = useState<MarginFloors>({});

  useEffect(() => {
    if (!user) {
      setFloors({});
      return;
    }
    let cancelled = false;
    void api<{ redFloor: number; amberFloor: number }>('/api/config').then((r) => {
      if (cancelled || !r.ok) return;
      const cfg = r.body as { redFloor?: number; amberFloor?: number };
      const next: MarginFloors = {};
      // typeof narrows to `number` (Number.isFinite alone does not under exactOptionalPropertyTypes),
      // so a missing/non-numeric floor stays absent and marginColor renders neutral rather than guessing.
      const { redFloor, amberFloor } = cfg;
      if (typeof redFloor === 'number' && Number.isFinite(redFloor)) next.redFloor = redFloor;
      if (typeof amberFloor === 'number' && Number.isFinite(amberFloor))
        next.amberFloor = amberFloor;
      setFloors(next);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const value = useMemo<ConfigContextValue>(() => ({ floors }), [floors]);
  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

/** The owner's live margin traffic-light floors. Empty until /api/config resolves. */
export function useMarginFloors(): MarginFloors {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useMarginFloors must be used within a ConfigProvider');
  return ctx.floors;
}
